import { useCreateClientMutation, useGetClientByIdQuery, useUpdateClientMutation } from '@endpoints/clientsEndpoint'
import {
  useCreateMarketingObjectiveMutation,
  useDeleteMarketingObjectiveMutation,
  useGetMarketingObjectivesQuery,
  useUpdateMarketingObjectiveMutation,
} from '@endpoints/marketingObjectivesEndpoint'
import { Client, HTLChannels, HTLObjective, Module, PickByType, Tenant } from '@types'
import { pendingToast, removeKeyFromArray, updateErrorToast, updateSuccessToast } from '@utils'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'
import { useValidation } from './useValidation'
import { toast } from 'react-toastify'
import { useSession } from './useSession'
import { useGetModulesQuery, useGetSiteV2DeploysQuery } from '@apis/mediacoreApi'
import { useGetSitesV2Query } from '@endpoints/sitesV2Endpoint'
import { useGetSiteCompilersQuery } from '@endpoints/siteCompilersEndpoint'

interface Props {
  id?: number
}

const createEmptyGestion = (): HTLChannels => ({
  mode: undefined,
  title: '',
  kpi: undefined,
})

const createEmptyObjective = (): HTLObjective => ({
  htl_level: undefined,
  title: '',
  kpi: undefined,
  total_duration: undefined,
  channels: [createEmptyGestion()],
})

export const useClient = ({ id }: Props) => {
  const { user } = useSession()

  const {
    data: queriedClient,
    isLoading: isLoadingClient,
    isFetching: _isFetchingClient,
    isError: isErrorClient,
    error: errorClient,
    refetch: refetchClient,
  } = useGetClientByIdQuery({ id: id! }, { skip: !user || !id })
  const blankClient: Partial<Tenant> = {
    colors: { primary: '#000000', secondary: '#000000', tertiary: '#000000' },
    modules: [],
    phones: { primaryPhone: '', whatsapp: '' },
    withholding_agent: false,
    domains: [],
    files_data: [],
    name: '',
    email: '',
    status: '',
    logo: '',
    agency_gantt: false,
    gantt_url: '',
    mailup: {
      username: '',
      password: '',
      client_id: '',
      client_secret: '',
      list_id: '',
    },
    xintel_keys: {
      inm: '',
      api_key: '',
    },
  }
  const {
    data: queriedModules,
    isLoading: _isLoadingModules,
    isError: _isErrorModules,
  } = useGetModulesQuery(void null, { skip: !user })
  const { data: sitesV2 } = useGetSitesV2Query({ clientId: id! }, { skip: !user || !id || queriedClient?.use_site_compiler })
  const { data: siteCompilers } = useGetSiteCompilersQuery(
    { clientId: id! },
    { skip: !user || !id || !queriedClient?.use_site_compiler },
  )
  const lastSiteId = useMemo(() => {
    const source = queriedClient?.use_site_compiler ? siteCompilers : sitesV2
    if (!source?.length) return undefined
    const withId = (source as Array<{ id?: number }>).filter(
      (s): s is { id: number } => typeof s?.id === 'number',
    )
    if (!withId.length) return undefined
    return [...withId].sort((a, b) => (b.id as number) - (a.id as number))?.[0]?.id
  }, [sitesV2, siteCompilers, queriedClient?.use_site_compiler])
  const { data: lastSiteDeploys } = useGetSiteV2DeploysQuery(
    { siteId: lastSiteId!, isSiteCompiler: queriedClient?.use_site_compiler },
    { skip: !lastSiteId },
  )
  const lastSiteDeploy = useMemo(() => {
    if (!Array.isArray(lastSiteDeploys) || lastSiteDeploys.length === 0) return undefined
    return [...lastSiteDeploys].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
  }, [lastSiteDeploys])

  const [client, setClient] = useState<Partial<Tenant>>(blankClient)
  const [htlObjectives, setHTLObjectives] = useState<HTLObjective[]>([])
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation()
  const [createClient, { isLoading: isCreating }] = useCreateClientMutation()
  const [createMarketingObjective] = useCreateMarketingObjectiveMutation()
  const [updateMarketingObjective] = useUpdateMarketingObjectiveMutation()
  const [deleteMarketingObjective] = useDeleteMarketingObjectiveMutation()
  const [_tenantLogoUrl, setTenantLogoUrl] = useState('')
  // const { data: logoColors } = usePalette(tenantLogoUrl)
  const isSaving = isUpdating || isCreating

  const marketingModuleId = queriedModules?.find((m) => m.code === 'MARKETING')?.id
  const hasMarketingModule = Boolean(marketingModuleId && (client.modules as number[])?.includes(marketingModuleId))

  const { data: marketingObjectives } = useGetMarketingObjectivesQuery(
    { clientId: id! },
    { skip: !user || !id || !hasMarketingModule },
  )

  useEffect(() => {
    if (queriedClient) {
      const queriedClientModules = queriedClient?.modules as Module[] // This value comes as Module[] from api
      const { htl_objectives: _htlObjectives, ...queriedClientData } = queriedClient
      setClient({
        ...queriedClientData,
        modules: queriedClientModules?.map((module) => module.id) ?? [],
      })
    }
  }, [queriedClient])

  useEffect(() => {
    if (!Array.isArray(marketingObjectives)) return
    setHTLObjectives(marketingObjectives)
  }, [marketingObjectives])

  useEffect(() => {
    setTenantLogoUrl(client?.logo instanceof File ? URL.createObjectURL(client?.logo) : '')
  }, [client?.logo])

  // useEffect(() => {
  //   if (client?.logo)
  //     setClient({
  //       ...client,
  //       colors: {
  //         primary: logoColors.vibrant ?? '#000000',
  //         secondary: logoColors.darkMuted ?? '#000000',
  //         tertiary: logoColors.lightMuted ?? '#000000',
  //       },
  //     })
  // }, [logoColors])

  const handleModuleSelection = (id: number) => {
    setClient((prevData) => ({
      ...prevData,
      modules: (prevData.modules as number[]).includes(id)
        ? (prevData.modules as number[]).filter((moduleId) => moduleId !== id)
        : [...(prevData.modules as number[]), id],
    }))
  }

  const toggleSwitch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target
    setClient((prevData) => ({
      ...prevData,
      [name]: !prevData[name as keyof typeof prevData],
    }))
  }

  const handleColorChange = (e: ChangeEvent<HTMLInputElement>, color: string) => {
    e.persist()
    const selectedColor = e.target.value

    setClient((prevData) =>
      prevData.colors
        ? {
            ...prevData,
            colors: {
              ...prevData.colors,
              [color]: selectedColor,
            },
          }
        : prevData,
    )
  }

  const handleModuleNotification = (moduleId: number) => {
    setClient((prevClient) => {
      const currentModules = Array.isArray(prevClient?.notifications_by_modules)
        ? prevClient.notifications_by_modules
        : []

      const updatedModules = currentModules.includes(moduleId)
        ? currentModules.filter((id) => id !== moduleId)
        : [...currentModules, moduleId]

      return {
        ...prevClient,
        notifications_by_modules: updatedModules,
      }
    })
  }

  const handleLogoInput = (files: File[] | null) => {
    if (files?.length) {
      setClient((prevData) => ({
        ...prevData,
        logo: files[0],
      }))
    }
  }

  const addFiles = (files: File[] | null, order: number) => {
    if (!files || files.length === 0) return

    const file = files[0]
    const label = order === 1 ? 'Presupuesto tentativo' : 'Propuesta comercial'

    setClient((prev) => {
      const filtered = (prev.files_data ?? []).filter((fd) => fd.order !== order)

      return {
        ...prev,
        files_data: [
          ...filtered,
          {
            file,
            file_name: file.name,
            order,
            label,
          },
        ],
      }
    })
  }

  const removeFile = (orderNumber: number) => {
    setClient((prevData) => ({
      ...prevData,
      files_data: (prevData?.files_data ?? []).filter((fd) => fd.order !== orderNumber),
    }))
  }

  const handleButtonLabel = (buttonNumber: number, value: string) => {
    setClient((prevData) => {
      const updatedFilesData = (prevData?.files_data ?? []).map((fd) =>
        fd.order === buttonNumber ? { ...fd, label: value } : fd,
      )

      return {
        ...prevData,
        files_data: updatedFilesData,
      }
    })
  }

  const saveMarketingObjectives = (clientId: number) => {
    if (!hasMarketingModule || !clientId) return Promise.resolve([])

    return Promise.all(
      htlObjectives.map((objective) => {
        const payload = {
          ...objective,
          client: clientId,
          channels: objective.channels ?? [],
        }
        return typeof objective.id === 'number'
          ? updateMarketingObjective({ id: objective.id, data: payload }).unwrap()
          : createMarketingObjective({ data: payload }).unwrap()
      }),
    ).then((syncedObjectives) => {
      setHTLObjectives(() => syncedObjectives)
      return syncedObjectives
    })
  }

  const addObjective = () => {
    setHTLObjectives((current) => [...current, createEmptyObjective()])
  }

  const removeObjective = (objectiveIndex: number) => {
    const objectiveToRemove = htlObjectives[objectiveIndex]
    if (!objectiveToRemove) return

    if (typeof objectiveToRemove.id !== 'number') {
      setHTLObjectives((current) => current.filter((_, index) => index !== objectiveIndex))
      return
    }

    deleteMarketingObjective({ id: objectiveToRemove.id })
      .unwrap()
      .then(() => {
        setHTLObjectives((current) => current.filter((_, index) => index !== objectiveIndex))
      })
      .catch(() => {
        toast.error('No se pudo eliminar el objetivo.')
      })
  }

  const handleObjectiveFieldChange = <K extends keyof HTLObjective>(
    objectiveIndex: number,
    field: K,
    value: HTLObjective[K],
  ) => {
    setHTLObjectives((current) =>
      current.map((objective, index) => (index === objectiveIndex ? { ...objective, [field]: value } : objective)),
    )
  }

  const addGestion = (objectiveIndex: number) => {
    setHTLObjectives((current) =>
      current.map((objective, index) =>
        index === objectiveIndex
          ? { ...objective, channels: [...(objective.channels ?? []), createEmptyGestion()] }
          : objective,
      ),
    )
  }

  const removeGestion = (objectiveIndex: number, gestionIndex: number) => {
    setHTLObjectives((current) =>
      current.map((objective, index) =>
        index === objectiveIndex
          ? { ...objective, channels: (objective.channels ?? []).filter((_, idx) => idx !== gestionIndex) }
          : objective,
      ),
    )
  }

  const handleGestionFieldChange = <K extends keyof HTLChannels>(
    objectiveIndex: number,
    gestionIndex: number,
    field: K,
    value: HTLChannels[K],
  ) => {
    setHTLObjectives((current) =>
      current.map((objective, index) =>
        index === objectiveIndex
          ? {
              ...objective,
              channels: (objective.channels ?? []).map((channel, idx) =>
                idx === gestionIndex ? { ...channel, [field]: value } : channel,
              ),
            }
          : objective,
      ),
    )
  }

  const { validationErrors } = useValidation({
    shape: {
      email: yup.string().email('Email inválido').required('El email es requerido'),
      name: yup.string().required('El nombre es requerido'),
      status: yup.string().required('El estado comercial es requerido'),
      logo: yup.mixed().test('required', 'El logo es requerido', (value) => {
        return value instanceof File || (value !== '' && typeof value === 'string')
      }),
      gantt_url: yup.string().matches(/src="([^"]+)"|^$/, 'El formato del iframe del Gantt no es válido'),
      files_data: yup.array().of(
        yup.object().shape({
          label: yup.string().required('El campo texto del botón es requerido'),
        }),
      ),
    },
    data: client,
  })

  const [backendValidationErrors, setBackendValidationErrors] = useState<Record<string, string>>({})

  const saveClient = () => {
    setBackendValidationErrors({})
    const { logo, files_data = [], htl_objectives: _htlObjectives, ...clientRest } = client
    const filesList = files_data.map((f) => f.file)

    const data = {
      ...clientRest,
      ...(logo instanceof File && { logo }),
      ...(filesList.length > 0 && { files: filesList }),
      files_data: removeKeyFromArray(client?.files_data ?? [], 'file'),
    }

    return new Promise((resolve, reject) => {
      const postOrPatch = () => {
        const toastId = pendingToast(toast, `${id ? 'Guardando' : 'Creando'} cliente "${data.name}" ...`)
        ;(id ? updateClient({ id, data }) : createClient({ data }))
          .unwrap()
          .then((savedClient) => {
            updateSuccessToast(toast, toastId, `${id ? 'Cliente editado' : 'Cliente creado'} con éxito`)
            resolve(savedClient)
          })
          .catch((error) => {
            const domainsError = error?.data?.domains?.[0]
            if (domainsError) setBackendValidationErrors({ domain: domainsError })
            updateErrorToast(
              toast,
              toastId,
              error.status === 403
                ? error?.data?.detail
                : `No se pudo ${id ? 'editar' : 'crear'} el cliente "${data.name}"`,
            )
            reject('error')
          })
      }

      postOrPatch()
    })
  }

  const setValue = <T>(name: keyof PickByType<Client, T>, value: T) => {
    setClient((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  const setPhoneValue = <T>(name: keyof Client['phones'], value: T) => {
    setClient((prevData) => ({
      ...prevData,
      phones: {
        ...prevData.phones,
        [name]: value,
      },
    }))
  }

  const setDomain = (value: string) => {
    setClient((prevData) => ({
      ...prevData,
      domains: [{ id: queriedClient?.domains[0]?.id, name: value }],
    }))
  }

  return {
    client,
    isLoadingClient,
    isSaving,
    isErrorClient,
    errorClient,
    refetchClient,
    validationErrors,
    backendValidationErrors,
    saveClient,
    saveMarketingObjectives,
    handleModuleSelection,
    toggleSwitch,
    handleLogoInput,
    handleColorChange,
    handleModuleNotification,
    handleButtonLabel,
    htlObjectives,
    addObjective,
    removeObjective,
    handleObjectiveFieldChange,
    addGestion,
    removeGestion,
    handleGestionFieldChange,
    setValue,
    setPhoneValue,
    setDomain,
    queriedModules,
    addFiles,
    removeFile,
    hasMarketingModule,
    lastSiteDeploy,
  }
}
